<template>
  <div class="w-full h-full">
    <SurveyBatchComponent
      v-if="apiToken && props.batchData.target_area_external_id"
      :target-area-id="props.batchData.target_area_external_id"
      :batch-uuid="props.batchData.batch_uuid || undefined"
      :country-code="props.batchData.country_iso3_code"
      :center-lat="props.batchData.center_lat"
      :center-lng="props.batchData.center_lng"
      :read-only="props.batchData.read_only"
      :surveyors="props.batchData.surveyors"
      :external-user-id="props.batchData.external_user_id"
      :batch-name-default="props.batchData.default_batch_name || undefined"
      :api-base-url="sparrowBaseUrl"
      :api-key="apiToken"
      :on-token-refresh="handleTokenRefresh"
      :on-auth-failure="handleAuthFailure"
      @batch-created="onBatchCreated"
      @batch-updated="onBatchUpdated"
      @surveyor-assigned="onSurveyorAssigned"
      @batch-deactivated="onBatchDeactivated"
      @error="onError"
    />
    <div v-else-if="!apiToken" class="flex items-center justify-center h-full text-gray-500">
      Loading...
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { SurveyBatch as SurveyBatchComponent } from '@ishare/kestrel-vue'
import '@ishare/kestrel-vue/style.css'
import 'maplibre-gl/dist/maplibre-gl.css'
import FlashMessageService from '@services/FlashMessages'
import { useSparrowAuth } from '@composables/useSparrowAuth'

const props = defineProps({
  batchData: {
    type: Object,
    required: true
  }
})

const sparrowBaseUrl = computed(() => (props.batchData.kestrel_api_url || '').replace(/\/api\/?$/, ''))
const countryIso3 = computed(() => props.batchData.country_iso3_code)

const { token: apiToken, fetchToken: fetchConstrainedToken, handleTokenRefresh, handleAuthFailure } = useSparrowAuth({
  countryIso3,
  capabilities: ['target_area.view', 'boundary.view:all', 'boundary.view_shading:all', 'batch.view', 'batch.create', 'batch.create_adhoc', 'batch.edit'],
  onError: () => FlashMessageService.error('Authentication failed. Please refresh the page.'),
})

async function onBatchCreated({ externalUuid, batchData }) {
  try {
    const response = await fetch('/batches', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        external_uuid: externalUuid,
        target_area_external_id: props.batchData.target_area_external_id,
        batch_data: batchData,
        due_date: batchData?.dueDate || null
      })
    })
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Failed to save batch')
    // Store the iMetrics batch ID so subsequent updates (surveyor assign, etc.) work
    props.batchData.imetrics_batch_id = result.id
    FlashMessageService.success('Batch created successfully')
  } catch (e) {
    FlashMessageService.error('Batch created in Sparrow but failed to save locally: ' + e.message)
  }
}

async function onBatchUpdated({ batchData }) {
  const batchId = props.batchData.imetrics_batch_id
  if (!batchId) return
  try {
    const response = await fetch(`/batches/${batchId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ batch_data: batchData })
    })
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Failed to update batch')
    FlashMessageService.success('Batch saved successfully')
  } catch (e) {
    FlashMessageService.error('Batch updated in Sparrow but failed to save locally: ' + e.message)
  }
}

async function onSurveyorAssigned(surveyorUuid) {
  const batchId = props.batchData.imetrics_batch_id
  if (!batchId) return
  try {
    const response = await fetch(`/batches/${batchId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ surveyor_uuid: surveyorUuid, state: 'ready_for_survey' })
    })
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Failed to update batch')
    // Update URL to edit page so reload preserves batch state (surveys, surveyor, etc.)
    history.replaceState(null, '', `/batches/${batchId}/edit`)
    FlashMessageService.success('Surveyor assigned and batch marked ready for survey')
  } catch (e) {
    FlashMessageService.error('Surveyor assigned in Sparrow but failed to save locally: ' + e.message)
  }
}

async function onBatchDeactivated() {
  const batchId = props.batchData.imetrics_batch_id
  if (batchId) {
    try {
      const response = await fetch(`/batches/${batchId}/deactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Failed to deactivate batch locally')
    } catch (e) {
      FlashMessageService.error('Batch deactivated in Sparrow but failed to update locally: ' + e.message)
      return
    }
  }
  window.location.href = props.batchData.redirect_after_deactivate_url
}

function onError(message) {
  FlashMessageService.error(message || 'An error occurred with the batch.')
}

onMounted(async () => {
  try {
    await fetchConstrainedToken()
  } catch (err) {
    console.error('Failed to fetch constrained token:', err)
    FlashMessageService.error('Failed to initialize batch view. Please refresh.')
  }
})
</script>
